import { useState } from "react";
import { useUser } from "../contexts/UserContext";
import { useNotices, Notice } from "../contexts/NoticeContext";

export default function NoticePage() {
  const { user } = useUser();
  const { notices, addNotice, updateNotice, deleteNotice } = useNotices();
  const [selected, setSelected] = useState<Notice | null>(null);
  const [showWrite, setShowWrite] = useState(false);
  const [editNotice, setEditNotice] = useState<Notice | null>(null);

  const [form, setForm] = useState({
    title: "",
    content: "",
    is_pinned: false,
    is_popup: false, // ✅ 팝업 여부 추가
  });

  const pinnedNotices = notices.filter(n => n.is_pinned);
  const otherNotices = notices.filter(n => !n.is_pinned);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editNotice) {
      await updateNotice({
        ...editNotice,
        title: form.title,
        content: form.content,
        is_pinned: form.is_pinned,
        is_popup: form.is_popup, // ✅ 추가
      });
      setEditNotice(null);
    } else {
      await addNotice({
        title: form.title,
        content: form.content,
        is_pinned: form.is_pinned,
        is_popup: form.is_popup, // ✅ 추가
      });
    }
    setShowWrite(false);
    setForm({ title: "", content: "", is_pinned: false, is_popup: false });
  }

  function startEdit(notice: Notice) {
    setEditNotice(notice);
    setShowWrite(true);
    setForm({
      title: notice.title,
      content: notice.content,
      is_pinned: !!notice.is_pinned,
      is_popup: !!notice.is_popup, // ✅ 추가
    });
  }

  async function handleDelete(id: number) {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      await deleteNotice(id);
      setSelected(null);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold mb-4">공지사항</h1>

      {user?.userType === "admin" && (
        <button
          className="mb-4 px-4 py-2 rounded bg-black text-white"
          onClick={() => {
            setShowWrite(true);
            setEditNotice(null);
            setForm({ title: "", content: "", is_pinned: false, is_popup: false });
          }}
        >
          새 공지 작성
        </button>
      )}

      {showWrite && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 border p-4 rounded flex flex-col gap-2 bg-gray-50"
        >
          <label>
            <span className="font-semibold">제목</span>
            <input
              className="w-full border rounded px-2 py-1 mt-1"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              required
            />
          </label>

          <label>
            <span className="font-semibold">내용</span>
            <textarea
              className="w-full border rounded px-2 py-1 mt-1"
              rows={5}
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              required
            />
          </label>

          {/* ✅ 기존 상단 고정 */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_pinned}
              onChange={e => setForm(f => ({ ...f, is_pinned: e.target.checked }))}
            />
            상단 고정
          </label>

          {/* ✅ 새로 추가: 팝업 표시 */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_popup}
              onChange={e => setForm(f => ({ ...f, is_popup: e.target.checked }))}
            />
            팝업으로 표시
          </label>

          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="px-4 py-2 rounded bg-black text-white"
            >
              {editNotice ? "공지 수정" : "공지 등록"}
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded border"
              onClick={() => {
                setShowWrite(false);
                setEditNotice(null);
                setForm({ title: "", content: "", is_pinned: false, is_popup: false });
              }}
            >
              취소
            </button>
          </div>
        </form>
      )}

      {pinnedNotices.length > 0 && (
        <div className="mb-4">
          {pinnedNotices.map(notice => (
            <div
              key={notice.id}
              className="border-l-4 border-[#E60023] bg-[#fdf1f3] p-4 mb-2 rounded cursor-pointer"
              onClick={() => setSelected(notice)}
            >
              <div className="text-xs text-[#E60023] font-bold mb-1">[공지]</div>
              <div className="font-bold">{notice.title}</div>
              <div className="text-xs text-gray-500">{notice.created_at?.slice(0,10)}</div>
              {user?.userType === "admin" && (
                <div className="mt-1 flex gap-2 text-xs">
                  <button className="underline" onClick={e => { e.stopPropagation(); startEdit(notice); }}>수정</button>
                  <button className="underline text-red-600" onClick={e => { e.stopPropagation(); handleDelete(notice.id); }}>삭제</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        {otherNotices.length === 0 && <div className="text-gray-400 text-sm">공지사항이 없습니다.</div>}
        {otherNotices.map(notice => (
          <div
            key={notice.id}
            className="border p-4 mb-2 rounded cursor-pointer"
            onClick={() => setSelected(notice)}
          >
            <div className="font-bold">{notice.title}</div>
            <div className="text-xs text-gray-500">{notice.created_at?.slice(0,10)}</div>
            {user?.userType === "admin" && (
              <div className="mt-1 flex gap-2 text-xs">
                <button className="underline" onClick={e => { e.stopPropagation(); startEdit(notice); }}>수정</button>
                <button className="underline text-red-600" onClick={e => { e.stopPropagation(); handleDelete(notice.id); }}>삭제</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-20">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => setSelected(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-black z-30"
            >
              닫기
            </button>
            <div className="font-bold text-lg mb-2">{selected.title}</div>
            <div className="text-xs text-gray-500 mb-4">
              {selected.created_at?.slice(0, 10)}
            </div>
            <div className="text-gray-800 whitespace-pre-line">
              {selected.content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
